import { AxiosResponse } from "axios";
import { endPoints } from "utils/constants";
import { crudService } from "services/crud.service";
import { NotificationManager } from "react-notifications";
import { defaultMetaObj, initialDashboardWidgetState } from "store/constants";
import { getErrorMessage, getPaginatedUrlWithSearchParams } from "store/helper";
import { createAsyncThunk, PayloadAction, createSlice } from "@reduxjs/toolkit";

const { userWidgetEndPoint } = endPoints;

export const fetchAllWidgets = createAsyncThunk(
  "dashboardWidgets/fetchAllWidgets",
  async (
    { page, searchParamsObj = {} }: { page: number; searchParamsObj?: {} },
    { rejectWithValue }
  ) => {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: page,
      endpoint: userWidgetEndPoint,
      searchParams: { ...searchParamsObj },
      limit: 50,
    });
    try {
      const response: AxiosResponse = await crudService.getAll(paginatedUrl);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const addWidget = createAsyncThunk(
  "dashboardWidgets/addWidget",
  async (widgetData: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.create(
        `${userWidgetEndPoint}/`,
        widgetData
      );

      if (response.status === 201) {
        NotificationManager.success("Widget Created Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const deleteWidget = createAsyncThunk(
  "dashboardWidgets/deleteWidget",
  async ({ widgetId }: any, { rejectWithValue }) => {
    try {
      const response: any = await crudService.delete(
        userWidgetEndPoint,
        widgetId
      );

      if (response.status === 204) {
        NotificationManager.success("Widget Deleted Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const updateWidget = createAsyncThunk(
  "dashboardWidgets/updateWidget",
  async ({ updatedData, widgetId }: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.update(
        userWidgetEndPoint,
        widgetId,
        updatedData
      );
      if (response.status === 204) {
        NotificationManager.success("Widget updated Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

const dashboardWidgetSlice = createSlice({
  name: "dashboardWidgets",
  initialState: initialDashboardWidgetState,
  reducers: {},
  extraReducers: (builder) => {
    // fetch all user Widgets
    builder
      .addCase(fetchAllWidgets.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchAllWidgets.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.widgetsArray = action.payload.objects as any[];
          state.meta = action.payload?.meta;
        }
      )
      .addCase(
        fetchAllWidgets.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
          state.meta = defaultMetaObj;
        }
      );

    // add Widget
    builder
      .addCase(addWidget.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(addWidget.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
      })
      .addCase(addWidget.rejected, (state: any, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // delete Widget
    builder
      .addCase(deleteWidget.pending, (State) => {
        State.loading = true;
      })
      .addCase(deleteWidget.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
      })
      .addCase(
        deleteWidget.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );

    //   update Widget
    builder
      .addCase(updateWidget.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        updateWidget.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
        }
      )
      .addCase(
        updateWidget.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );
  },
});

export const dashboardWidgetReducer = dashboardWidgetSlice.reducer;
